import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { combineLatest, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { debounceTime, filter, finalize, switchMap, take } from 'rxjs/operators';
import { fadeAnimation, showAnimation } from 'src/animations';
import { modules } from 'src/app/globals';
import { SidenavOption } from 'src/app/lib/shared/classes';
import { CompleteRequestDialogComponent, CompleteRequestDialogComponentInputData } from 'src/app/lib/shared/components/dialog/complete-request-dialog/complete-request-dialog.component';
import { DoubleCheckDialogComponent, DoubleCheckDialogComponentInputData } from 'src/app/lib/shared/components/dialog/double-check-dialog/double-check-dialog.component';
import { HereMapDialogComponent, HereMapDialogComponentData } from 'src/app/lib/shared/components/dialog/here-map-dialog/here-map-dialog.component';
import { CONCURRENCY_PROVIDER_SERVICE } from 'src/app/lib/shared/interfaces';
import { SessionService } from 'src/app/lib/shared/services/session.service';
import { SidenavService } from 'src/app/lib/shared/services/sidenav.service';
import { Address, Location } from '../../../classes';
import { AVAILABILITIES_PROVIDER_SERVICE, LOCATION_PROVIDER_SERVICE } from '../../../interfaces';
import { LocationService } from '../../../services/location.service';
import { LocationDetailViewComponentService } from './location-detail-view-component-service';

@Component({
  selector: 'app-location-detail-view',
  templateUrl: './location-detail-view.component.html',
  styleUrls: ['./location-detail-view.component.css'],
  animations: [showAnimation, fadeAnimation],
  providers: [
    { provide: LOCATION_PROVIDER_SERVICE, useClass: LocationDetailViewComponentService },
    { provide: CONCURRENCY_PROVIDER_SERVICE, useExisting: LOCATION_PROVIDER_SERVICE },
    { provide: AVAILABILITIES_PROVIDER_SERVICE, useExisting: LOCATION_PROVIDER_SERVICE },
    SidenavService
  ]
})
export class LocationDetailViewComponent implements OnInit {

  disabled: boolean = false;

  modules = modules;

  formGroup: FormGroup;

  reset: boolean = false;
  
  rightsMap = {
    "delete_locations": false
  };

  private _sidenavOptions = {
    'editMode': {
      'icon': () => this.disabled ? 'edit' : 'edit_off',
      'title': () => this.disabled ? 'Bearbeiten' : 'Sperren',
      'onClick': () => of(this.toggleEditMode()),
      'visible': () => true,
      'appearance': () => 'default'
    } as SidenavOption,
    'save': {
      'icon': () => this.formGroup.dirty ? 'save' : 'done',
      'title': () => this.formGroup.dirty ? 'Speichern' : 'Aktuell',
      'onClick': () => this.formGroup.dirty ? this.saveChanges() : of(null),
      'visible': () => !this.disabled,
      'appearance': () => this.formGroup.dirty ? 'success' : 'ok'
    } as SidenavOption,
    'discard': {
      'icon': () => 'clear',
      'title': () => 'Verwerfen',
      'onClick': () => this.discardChanges(),
      'visible': () => !this.disabled && this.formGroup.dirty,
      'appearance': () => 'danger'
    } as SidenavOption,
    'watched_status': {
      'icon': () => this.formGroup.controls['isWatched'].value ? 'visibility' : 'visibility_off',
      'title': () => this.formGroup.controls['isWatched'].value ? 'Beobachtet' : 'Nicht beob.',
      'onClick': () => this.toggleWatchedStatus(),
      'visible': () => true,
      'appearance': () => 'dark'
    } as SidenavOption,
    'delete': {
      'icon': () => 'delete_forever',
      'title': () => 'Löschen',
      'onClick': () => this.deleteLocation(),
      'visible': () => !this.disabled && this.rightsMap.delete_locations,
      'appearance': () => 'danger'
    } as SidenavOption
  };

  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(LOCATION_PROVIDER_SERVICE) private locationDetailViewComponentService: LocationDetailViewComponentService,
    private _sidenavService: SidenavService,
    private _formBuilder: FormBuilder,
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private _sessionService: SessionService,
    private _locationService: LocationService
  ) { }

  afterStoring() {
    this.locationDetailViewComponentService.validateAvailabilities().subscribe(() => {
      (this.formGroup.controls['availabilities'] as FormArray).clear();
      this.formGroup.markAsUntouched();
      this.formGroup.markAsPristine();
      this.locationDetailViewComponentService.refreshCurrentLocation();
      this._snackBar.open('Änderungen erfolgreich gespeichert!', 'Speichern', {
        duration: 2000
      });
    });
  }

  clearLocationFloor() {
    this.locationFloorControl.setValue(null);
    this.locationFloorControl.markAsDirty();
  }

  deleteLocation() {
    let subject = new Subject();
    this.locationDetailViewComponentService.location$.pipe(take(1)).subscribe((location) => {
      let number = Math.floor(1000000 + Math.random() * 9000000).toFixed(0);
      this._dialog.open(DoubleCheckDialogComponent, {
        autoFocus: false,
        panelClass: 'no-padding-dialog-container',
        data: {
          mode: 'numeric',
          title: 'Löschen bestätigen',
          text: `Bitte geben Sie die Nummer ${number} ein um den Standort dauerhaft zu löschen`,
          doubleCheckValue: number
        } as DoubleCheckDialogComponentInputData
      }).afterClosed().subscribe((result) => {
        if (result) {
          this.locationDetailViewComponentService.deleteCurrentLocation().subscribe(subject);
        } else subject.complete();
      });
    });
    return subject.asObservable();
  }

  discardChanges(): Observable<Object> {
    this.reset = true;
    return this.locationDetailViewComponentService.refreshCurrentLocation();
  }

  initRights = (soft: boolean = true) => {
    for (let index in this.rightsMap) this._sessionService.hasRight(index, soft).subscribe(result => this.rightsMap[index] = result);
  }

  ngOnDestroy(): void {
    for (let sub of this._subscriptions) {
      sub.unsubscribe();
      this._subscriptions = [];
    }
    this.locationDetailViewComponentService.dispose();
  }

  ngOnInit(): void {

    this.formGroup = this._formBuilder.group({
      location: this._formBuilder.group(new Location()),
      address: this._formBuilder.group(new Address()),
      availabilities: this._formBuilder.array([]),
      isWatched: this._formBuilder.control(false)
    });

    this._sidenavService.setOptions(Object.values(this._sidenavOptions));

    this._subscriptions.push(...[
      combineLatest([this.formGroup.controls['availabilities'].valueChanges]).pipe(debounceTime(100)).subscribe(([value]) => {
        if (this.formGroup.pristine && value.length > 0) this.formGroup.markAsDirty();
      }),
      this.locationDetailViewComponentService.location$.pipe(filter(x => this.formGroup.pristine || this.reset)).subscribe((location: Location) => this.setLocation(location)),
    ]);

    this.initRights();

  }

  saveChanges() {
    let subject = new Subject();

    combineLatest([
      this._dialog.open(CompleteRequestDialogComponent, {
        panelClass: 'no-padding-dialog-container',
        data: {
          addNote: true
        } as CompleteRequestDialogComponentInputData
      }).afterClosed(),
      this.locationDetailViewComponentService.location$
    ])
      .pipe(take(1))
      .subscribe(([output, location]) => {
        if (output.abort) {
          subject.complete();
          return;
        }

        this.locationDetailViewComponentService
          .saveChanges(
            this.formGroup.controls['location'].value,
            output.note,
            this.formGroup.controls['address'].value,
            this.formGroup.controls['availabilities'].value
          )
          .pipe(finalize(() => this.afterStoring()))
          .subscribe(subject);
      });

    return subject.asObservable();
  }

  setLocation(location: Location) {
    this.reset = false;
    this.formGroup.controls['location'].patchValue(location);
    this.formGroup.controls['address'].patchValue(location.address);
    (this.formGroup.controls['availabilities'] as FormArray).clear();
    this.formGroup.markAsPristine();
    timer(100).pipe(switchMap(() => this._locationService.locationIsWatched(location.locationid))).subscribe((result: boolean) => this.formGroup.controls['isWatched'].setValue(result));
    this._locationService.saveHistory(location.locationid);
  }

  showAddress = () => this._dialog.open(HereMapDialogComponent, {
    panelClass: 'no-padding-dialog-container',
    data: { address: this.formGroup.controls['address'].value } as HereMapDialogComponentData
  });

  toggleEditMode() {
    let pristine = this.formGroup.pristine;
    this.formGroup[this.disabled ? 'enable' : 'disable']({ emitEvent: false });
    this.disabled = !this.disabled;
    if (pristine) this.formGroup.markAsPristine();
  }

  toggleWatchedStatus() {
    let value = this.formGroup.controls['isWatched'].value ? true : false;
    this.locationDetailViewComponentService.locationId$.pipe(switchMap((locationId: number) => this._locationService.setLocationWatchedStatus(locationId, !value)))
      .subscribe(() => this.formGroup.controls['isWatched'].setValue(!value));

    return of(null);
  }

  @HostListener('keydown', ['$event'])
  private _keydown(event: KeyboardEvent){
    if(event.ctrlKey && event.key === 's'){
      event.preventDefault();
      event.stopPropagation();
      if(this.formGroup.dirty) this.saveChanges();
      else this._snackBar.open('Keine Änderungen zum Speichern erkannt', 'Ok', {
        duration: 2000
      });
    }
  }

  get addressControl(): AbstractControl {
    return this.formGroup.controls['address'];
  }

  get locationFloorControl(): AbstractControl {
    return (this.formGroup.controls['location'] as FormGroup).controls['locationfloor'];
  }

}
